import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { track } from '@/utils/tracker';

interface ToolItem {
  id: string;
  icon?: string;
  image?: string;
  titleKey: string;
  iconBg: string;
  route?: string;
}

const tools: ToolItem[] = [
  {
    id: 'text2img',
    image: 'https://storage.readdy-site.link/project_files/323de9e6-f67a-475e-b23e-36a900c626e1/be06d9cf-87f8-4006-881a-c3f3a86c0b1a_ChatGPT-Image-2026529-18_51_22.png?v=f7ac50f9c358655476f0b67a13e655c4',
    titleKey: 'tool_text2img',
    iconBg: 'linear-gradient(135deg, #F472B6, #EC4899)',
  },
  {
    id: 'img2img',
    image: 'https://storage.readdy-site.link/project_files/323de9e6-f67a-475e-b23e-36a900c626e1/0795f567-a726-4170-8aa0-8f104e6c1a88_ChatGPT-Image-2026529-18_54_16.png?v=f8563c9038a5295cab97311024ada435',
    titleKey: 'tool_img2img',
    iconBg: 'linear-gradient(135deg, #FB923C, #F97316)',
  },
  {
    id: 'edit',
    image: 'https://storage.readdy-site.link/project_files/323de9e6-f67a-475e-b23e-36a900c626e1/75499839-f19a-4397-85ea-b5c2f0cbf5fd_ChatGPT-Image-2026529-18_57_41.png?v=602febe1d3dd5901b2d07735d84b23b3',
    titleKey: 'tool_edit',
    iconBg: 'linear-gradient(135deg, #34D399, #10B981)',
  },
  {
    id: 'restore',
    image: 'https://storage.readdy-site.link/project_files/323de9e6-f67a-475e-b23e-36a900c626e1/3a598f1b-d702-4573-9238-57787e4ad86c_ChatGPT-Image-2026529-19_04_09.png?v=9e04ff03edbe47a04e9b4fab80814d79',
    titleKey: 'tool_restore',
    iconBg: 'linear-gradient(135deg, #818CF8, #6366F1)',
  },
  {
    id: 'tryon',
    image: 'https://storage.readdy-site.link/project_files/323de9e6-f67a-475e-b23e-36a900c626e1/58dd4388-0f64-44a9-b47c-e135e35e7bd2_ChatGPT-Image-2026529-18_58_10.png?v=cfa1e12d4543b9c857d896a7fb8a5855',
    titleKey: 'tool_tryon',
    iconBg: 'linear-gradient(135deg, #F472B6, #EC4899)',
  },
  {
    id: 'bg_replace',
    image: 'https://storage.readdy-site.link/project_files/323de9e6-f67a-475e-b23e-36a900c626e1/e50a95b4-60c4-4d5a-a539-6a37f3f797ad_ChatGPT-Image-2026529-19_06_03.png?v=8c6f294c65062c715378482d87b02faf',
    titleKey: 'extool_bgReplace',
    iconBg: 'linear-gradient(135deg, #06B6D4, #22D3EE)',
    route: '/bg-replace',
  },
  {
    id: 'outpaint',
    image: 'https://storage.readdy-site.link/project_files/323de9e6-f67a-475e-b23e-36a900c626e1/cbb41805-d853-4ce8-aeb4-cbbd2666dda3_ChatGPT-Image-2026529-19_12_10.png?v=4eb6c51877cf4e43bad24beccaff79dc',
    titleKey: 'extool_outpaint',
    iconBg: 'linear-gradient(135deg, #8B5CF6, #A78BFA)',
    route: '/outpaint',
  },
  {
    id: 'more',
    image: 'https://storage.readdy-site.link/project_files/323de9e6-f67a-475e-b23e-36a900c626e1/58e26bf2-430a-489a-a69e-c8f8911f9795_ChatGPT-Image-2026529-19_05_48.png?v=5bc9d6d4344353c51af2d3ca661e0127',
    titleKey: 'tool_more',
    iconBg: 'linear-gradient(135deg, #A78BFA, #8B5CF6)',
    route: '/tools',
  },
];

export default function AIToolsGrid() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    tools.forEach((tool) => {
      track.homeEntryExposure({
        entry_id: `generate_${tool.id}`,
        entry_name: t(tool.titleKey),
        source: 'home_tools',
      });
    });
  }, [t]);

  const handleToolClick = (tool: ToolItem) => {
    track.homeEntryClick({
      entry_id: `generate_${tool.id}`,
      entry_name: t(tool.titleKey),
      source: 'home_tools',
    });
    if (tool.route) {
      navigate(`${tool.route}?from=home`, { state: { returnTo: '/' } });
    } else {
      navigate(`/generate?mode=${tool.id}`);
    }
  };

  return (
    <div className="px-4">
      {/* 标题 */}
      <h2 className="text-sm font-bold text-gray-900 tracking-tight mb-3">{t('home_aiTools')}</h2>

      <div className="grid grid-cols-4 gap-y-4 gap-x-2">
        {tools.map((tool) => (
          <button
            key={tool.id}
            onClick={() => handleToolClick(tool)}
            className="flex flex-col items-center gap-1.5 cursor-pointer active:scale-90 transition-transform"
          >
            <div
              className="w-12 h-12 flex items-center justify-center overflow-hidden rounded-2xl"
              style={{
                background: tool.image ? 'transparent' : tool.iconBg,
              }}
            >
              {tool.image ? (
                <img src={tool.image} alt="" className="w-12 h-12 object-cover" />
              ) : (
                <i className={`${tool.icon} text-white text-xl`} />
              )}
            </div>
            <span className="text-[11px] text-gray-600 font-medium whitespace-nowrap">
              {t(tool.titleKey)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
